import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { api } from '@/lib/api'
import { money } from '@/lib/money'
import Button from '@/components/ui/Button'
import Card from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'

interface TopupStatus {
  status: 'pending' | 'paid' | 'failed'
  amount: number
  reference: string
  paid_at: string | null
}

const POLL_INTERVAL_MS = 2000
const POLL_ATTEMPTS = 15

/**
 * Where the gateway sends the customer back to.
 *
 * The browser almost always arrives before the gateway's webhook does, so this
 * cannot assume the payment succeeded just because the customer got here. It
 * polls the top-up until the webhook lands, and says so plainly if it has not
 * arrived by the time we stop asking.
 */
export default function TopUpReturn() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [params] = useSearchParams()
  const [status, setStatus] = useState<TopupStatus | null>(null)
  const [timedOut, setTimedOut] = useState(false)

  const topupId = params.get('topup_id') ?? sessionStorage.getItem('mpg_pending_topup')

  useEffect(() => {
    if (!topupId) {
      setTimedOut(true)
      return
    }

    let attempts = 0
    let cancelled = false

    const poll = async () => {
      if (cancelled) return
      try {
        const { data } = await api.get<TopupStatus>(`/wallet/topups/${topupId}`)
        if (cancelled) return
        setStatus(data)
        if (data.status === 'paid' || data.status === 'failed') {
          sessionStorage.removeItem('mpg_pending_topup')
          void queryClient.invalidateQueries({ queryKey: ['wallet'] })
          void queryClient.invalidateQueries({ queryKey: ['wallet-tx'] })
          return
        }
      } catch {
        // Keep polling: a transient error here should not look like a failed
        // payment when the money may well have been taken.
      }

      attempts += 1
      if (attempts >= POLL_ATTEMPTS) {
        setTimedOut(true)
        return
      }
      window.setTimeout(poll, POLL_INTERVAL_MS)
    }

    void poll()

    return () => {
      cancelled = true
    }
  }, [topupId, queryClient])

  const paid = status?.status === 'paid'
  const failed = status?.status === 'failed'

  return (
    <div>
      <PageHeader title="Wallet top-up" subtitle="Confirming your payment with the gateway" />
      <Card>
        <div className="py-8 text-center">
          {paid ? (
            <>
              <CheckCircle2 className="mx-auto size-10 text-status-success" />
              <p className="mt-3 text-lg font-semibold">Top-up complete</p>
              <p className="mt-1 text-sm text-muted">
                {money(status?.amount)} has been added to your wallet. Reference {status?.reference}.
              </p>
            </>
          ) : failed ? (
            <>
              <XCircle className="mx-auto size-10 text-status-danger" />
              <p className="mt-3 text-lg font-semibold">Payment not completed</p>
              <p className="mt-1 text-sm text-muted">
                Nothing was charged and your wallet is unchanged. You can try again whenever you are ready.
              </p>
            </>
          ) : timedOut ? (
            <>
              <Loader2 className="mx-auto size-10 text-muted" />
              <p className="mt-3 text-lg font-semibold">Still waiting on the bank</p>
              <p className="mt-1 text-sm text-muted">
                This can take a few minutes. If money left your account, the wallet updates on its own as soon as
                the gateway confirms it - there is no need to pay again.
              </p>
            </>
          ) : (
            <>
              <Loader2 className="mx-auto size-10 animate-spin text-brand-500" />
              <p className="mt-3 text-lg font-semibold">Confirming your payment...</p>
              <p className="mt-1 text-sm text-muted">Please do not pay again while this finishes.</p>
            </>
          )}

          <div className="mt-6 flex justify-center gap-2">
            <Button onClick={() => navigate('/account/wallet')}>Back to wallet</Button>
            {(failed || timedOut) && (
              <Button variant="outline" onClick={() => navigate('/account/wallet/topup')}>
                Try again
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  )
}
